import { useEffect, useState } from 'react';
import { alert, haptic } from '../lib/telegram';
import { fmtMoney, useT } from '../lib/i18n';
import { fmtShortDay, fmtTime } from '../lib/time';
import { PinnedSheet } from './SetSheet';
import { GrowArea } from './GrowArea';

/**
 * Clients without Telegram (sql/030): adding one by hand, and later
 * linking the manual profile to the person once they open the coach's
 * link («Привязать к Telegram»).
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setOfflineToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

/** «+ Добавить клиента вручную»: only the name is needed. */
export function AddClientSheet({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const t = useT();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const c = await api<{ id: string }>('/api/coach/clients', {
        method: 'POST', body: JSON.stringify({ name, phone, note }),
      });
      haptic.success();
      onAdded(c.id);
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={t('co.off.addTitle')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.off.cancel')}</button>
        <button className="savebtn" disabled={busy || !name.trim()} onClick={add}>
          {busy ? t('co.off.adding') : t('co.off.addBtn')}
        </button>
      </>}>
      <label className="mf">
        <span className="mf__label">{t('co.off.name')}</span>
        <input className="notearea off-input" value={name} autoFocus maxLength={80}
          onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="mf" style={{ marginTop: 12 }}>
        <span className="mf__label">{t('co.off.phone')} <i className="off-opt">{t('co.off.optional')}</i></span>
        <input className="notearea off-input" type="tel" inputMode="tel" value={phone} maxLength={40}
          onChange={(e) => setPhone(e.target.value)} />
      </label>
      <label className="mf" style={{ marginTop: 12 }}>
        <span className="mf__label">{t('co.off.note')} <i className="off-opt">{t('co.off.optional')} · {t('co.off.onlyYou')}</i></span>
        <GrowArea className="notearea" rows={2} value={note} placeholder={t('co.off.notePh')}
          onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="off-info">{t('co.off.info')}</div>
    </PinnedSheet>
  );
}

interface Candidate {
  id: string; name: string; username: string | null; phone: string | null;
  since: string; approved: boolean; match: 'phone' | 'name' | null;
}

/** «Привязать к Telegram», step one: which of the coach's Telegram clients this is. */
export function LinkPickSheet({ offline, onClose, onPick }: {
  offline: { id: string; name: string }; onClose: () => void; onPick: (c: { id: string; name: string; username: string | null }) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [list, setList] = useState<Candidate[] | null>(null);

  useEffect(() => {
    const h = setTimeout(() => {
      api<Candidate[]>(`/api/coach/clients/${offline.id}/link-candidates${q.trim() ? `?search=${encodeURIComponent(q.trim())}` : ''}`)
        .then(setList).catch(() => setList([]));
    }, q ? 250 : 0);
    return () => clearTimeout(h);
  }, [q, offline.id]);

  const likely = (list ?? []).filter((c) => c.match);
  const others = (list ?? []).filter((c) => !c.match);
  const row = (c: Candidate) => (
    <button key={c.id} className={`off-cand${c.match ? ' off-cand--match' : ''}`}
      onClick={() => { haptic.tap(); onPick(c); }}>
      <b>{c.name}</b>
      <span className="card__when">
        {c.username ? `@${c.username} · ` : ''}
        {c.approved ? t('co.off.since', { day: fmtShortDay(new Date(c.since)) }) : t('co.off.waiting')}
      </span>
      {c.match && (
        <span className="off-why">{c.match === 'phone' ? `${t('co.off.samePhone')} ${c.phone ?? ''}` : t('co.off.sameName')}</span>
      )}
    </button>
  );

  return (
    <PinnedSheet title={t('co.off.pickTitle')} onClose={onClose}
      footer={<button className="paybar__opt" onClick={onClose}>{t('co.off.cancel')}</button>}>
      <div className="card__when">{t('co.off.pickWho', { name: offline.name })}</div>
      <input className="clients__search off-search" type="search" value={q}
        placeholder={t('co.off.pickSearch')} onChange={(e) => setQ(e.target.value)} />
      {list === null ? <div className="skeleton" /> : list.length === 0 ? (
        <div className="off-info">{q ? t('co.off.noneFound') : t('co.off.nobodyYet')}</div>
      ) : (
        <>
          {likely.length > 0 && <div className="off-sec">{t('co.off.alike')}</div>}
          {likely.map(row)}
          {others.length > 0 && <div className="off-sec">{t('co.off.others')}</div>}
          {others.map(row)}
          <div className="card__when" style={{ marginTop: 8 }}>{t('co.off.onlyBot')}</div>
        </>
      )}
    </PinnedSheet>
  );
}

interface Preview {
  sessions: number; upcoming: number; credits: number; packages: number;
  payments: number; owed: number; notes: number; clashes: string[];
}

/** Step two: what moves over, and the one thing that stops it. */
export function LinkConfirmSheet({ offline, into, onClose, onLinked }: {
  offline: { id: string; name: string }; into: { id: string; name: string; username: string | null };
  onClose: () => void; onLinked: (r: { into: string; upcoming: number }) => void;
}) {
  const t = useT();
  const [p, setP] = useState<Preview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Preview>(`/api/coach/clients/${offline.id}/link-preview?into=${into.id}`)
      .then(setP).catch((e) => setErr(e.message));
  }, [offline.id, into.id]);

  async function link() {
    setBusy(true);
    try {
      const r = await api<{ into: string; upcoming: number }>(`/api/coach/clients/${offline.id}/link`, {
        method: 'POST', body: JSON.stringify({ into: into.id }),
      });
      haptic.success();
      onLinked(r);
    } catch (e: any) {
      haptic.error();
      await alert(t('co.failed', { message: e.message }));
    } finally { setBusy(false); }
  }

  const clash = p?.clashes?.[0];
  const lines = p ? [
    p.sessions > 0 && t('co.off.mvSessions', { n: p.sessions, sessions: t.word('co.today.sessions', p.sessions) })
      + (p.upcoming > 0 ? t('co.off.mvUpcoming', { n: p.upcoming }) : ''),
    p.credits !== 0 && t('co.off.mvCredits', { n: Math.round(p.credits * 100) / 100 }),
    p.packages > 0 && t('co.off.mvPackages', { n: p.packages }),
    p.payments > 0 && t('co.off.mvPayments', { n: p.payments }),
    p.owed > 0 && t('co.off.mvOwed', { amount: fmtMoney(Math.round(p.owed)) }),
    p.notes > 0 && t('co.off.mvNotes'),
    t('co.off.mvRest'),
  ].filter(Boolean) as string[] : [];

  return (
    <PinnedSheet title={t('co.off.confirmTitle', { name: offline.name })} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{clash ? t('co.off.close') : t('co.off.cancel')}</button>
        <button className="savebtn" disabled={busy || !p || !!clash} onClick={link}>{t('co.off.linkBtn')}</button>
      </>}>
      <div className="off-pair">
        <div className="off-who"><b>{offline.name}</b><span>{t('co.off.manual')}</span></div>
        <span aria-hidden="true">→</span>
        <div className="off-who"><b>{into.name}</b><span>{into.username ? `@${into.username} · ` : ''}{t('co.off.inBot')}</span></div>
      </div>
      {err ? <div className="off-clash">{t('co.failed', { message: err })}</div>
        : !p ? <div className="skeleton" />
        : clash ? (
          <div className="off-clash">{t('co.off.clash', { when: `${fmtShortDay(new Date(clash))}, ${fmtTime(clash)}` })}</div>
        ) : (
          <>
            <div className="off-sec">{t('co.off.moves', { name: into.name })}</div>
            <ul className="off-moves">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
            <div className="off-info">{t('co.off.keep', { name: offline.name })}</div>
          </>
        )}
    </PinnedSheet>
  );
}
